import { useEffect, useState } from "react";
import { Info, Quote, ShieldCheck } from "lucide-react";
import { api, track } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

let cache = null;
export function invalidateTrust() {
  cache = null;
  window.dispatchEvent(new Event("nxt-trust-updated"));
}
export function useTrust() {
  const [t, setT] = useState(cache);
  useEffect(() => {
    const load = () => api.get("/config/trust").then(({ data }) => { cache = data; setT(data); }).catch(() => {});
    if (!cache) load();
    window.addEventListener("nxt-trust-updated", load);
    return () => window.removeEventListener("nxt-trust-updated", load);
  }, []);
  return t;
}

export function GuidanceNote({ className = "", compact = false, testId = "guidance-note" }) {
  return (
    <p data-testid={testId} className={`flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground ${className}`}>
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
      <span>{compact ? "Guidance, not guarantees." : "Guidance, not guarantees — a strong, honest starting point, never a promise about jobs, salaries or outcomes."}</span>
    </p>
  );
}

export function HowItWorks({ className = "", label = "How this works", testId = "how-it-works-link" }) {
  const [open, setOpen] = useState(false);
  const t = useTrust();
  return (
    <>
      <button onClick={() => { setOpen(true); track("how_it_works_opened"); }} data-testid={testId} className={`inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:underline ${className}`}><Info className="h-3.5 w-3.5" /> {label}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="how-it-works-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold">How your Career DNA is scored</DialogTitle>
            <DialogDescription className="text-xs">Plain language, no black box. An unexplained score is a horoscope — this one you can check.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm leading-relaxed">
            {(t?.how_it_works || []).map((p, i) => (
              <li key={i} className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 font-mono text-xs font-bold text-white">{i + 1}</span><span>{p}</span></li>
            ))}
          </ol>
          <GuidanceNote className="mt-2" testId={`${testId}-guidance`} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function Testimonials({ compact = false, limit = 3 }) {
  const t = useTrust();
  const items = (t?.testimonials || []).filter((x) => x?.quote && x.placeholder === false).slice(0, limit);
  if (items.length === 0) return <div data-testid="career-benefits" className={`grid gap-3 ${compact ? "" : "sm:grid-cols-3"}`}>{[
    ["Understand what fits", "Explore career matches through your values, thinking style and starting point."],
    ["Turn direction into action", "Build momentum with a practical roadmap, daily missions and skill-building quests."],
    ["Prepare with your own evidence", "Keep useful advice in your Notebook and practise telling the story of your real work."],
  ].slice(0, limit).map(([title, text], i) => <div key={title} data-testid={`career-benefit-${i}`} className="rounded-3xl border border-border bg-card p-5"><h3 className="font-display text-sm font-bold">{title}</h3><p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p></div>)}</div>;
  return (
    <div data-testid="testimonials" className={`grid gap-3 ${compact ? "" : "sm:grid-cols-3"}`}>
      {items.map((x, i) => (
        <figure key={i} data-testid={`testimonial-${i}`} className="rounded-3xl border border-border bg-card p-4">
          <Quote className="h-4 w-4 text-indigo-400" />
          <blockquote className="mt-2 text-sm leading-relaxed">“{x.quote}”</blockquote>
          <figcaption className="mt-3 text-xs text-muted-foreground"><span className="font-bold text-foreground">{x.name}</span>{x.role ? ` · ${x.role}` : ""}</figcaption>
        </figure>
      ))}
    </div>
  );
}
